import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import BackButton from '../../components/common/BackButton';
import './ManageEvaluations.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

const ManageEvaluations = () => {
    const { t, i18n } = useTranslation();
    const [stats, setStats] = useState(null);
    const [evaluations, setEvaluations] = useState([]);
    const [filteredEvaluations, setFilteredEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [negativeFilterType, setNegativeFilterType] = useState('all');
    const [expandedRow, setExpandedRow] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, listRes] = await Promise.all([
                    axios.get('/api/evaluations/stats'),
                    axios.get('/api/evaluations')
                ]);
                setStats(statsRes.data.data);
                setEvaluations(listRes.data.data);
                setFilteredEvaluations(listRes.data.data);
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);


    useEffect(() => {
        if (filterType === 'all') {
            setFilteredEvaluations(evaluations);
        } else if (filterType === 'negative') {
            let negative = evaluations.filter(evalItem => {
                return (
                    evalItem.parking === 'Yes' ||
                    evalItem.waitingTime === 'Long' ||
                    evalItem.generalAppearance === 'Bad' ||
                    evalItem.doctorTime === 'No' ||
                    evalItem.treatmentPlan === 'No' ||
                    evalItem.nurseRating === 'Poor'
                );
            });

            if (negativeFilterType !== 'all') {
                negative = negative.filter(evalItem => {
                    switch (negativeFilterType) {
                        case 'parking':
                            return evalItem.parking === 'Yes';
                        case 'waitingTime':
                            return evalItem.waitingTime === 'Long';
                        case 'generalAppearance':
                            return evalItem.generalAppearance === 'Bad';
                        case 'doctorTime':
                            return evalItem.doctorTime === 'No';
                        case 'treatmentPlan':
                            return evalItem.treatmentPlan === 'No';
                        case 'nurseRating':
                            return evalItem.nurseRating === 'Poor';
                        default:
                            return true;
                    }
                });
            }

            setFilteredEvaluations(negative);
        } else if (filterType === 'positive') {
            const positive = evaluations.filter(evalItem => {
                return (
                    (evalItem.parking === 'No' || evalItem.parking === 'Sometimes') &&
                    (evalItem.waitingTime === 'Fast' || evalItem.waitingTime === 'Acceptable') &&
                    (evalItem.generalAppearance === 'Excellent' || evalItem.generalAppearance === 'Acceptable') &&
                    (evalItem.doctorTime === 'Yes' || evalItem.doctorTime === 'Sometimes') &&
                    (evalItem.treatmentPlan === 'Yes' || evalItem.treatmentPlan === 'A little') &&
                    (evalItem.nurseRating === 'Excellent' || evalItem.nurseRating === 'Acceptable')
                );
            });
            setFilteredEvaluations(positive);
        }
    }, [filterType, negativeFilterType, evaluations]);

    const handleDeleteAll = async () => {
        if (window.confirm(t('admin.confirmDeleteAllEvaluations', 'هل أنت متأكد من حذف جميع التقييمات؟ هذا الإجراء لا يمكن التراجع عنه.'))) {
            try {
                await axios.delete('/api/evaluations/all');
                setEvaluations([]);
                setStats({
                    parking: {},
                    waitingTime: {},
                    generalAppearance: {},
                    doctorTime: {},
                    treatmentPlan: {},
                    nurseRating: {},
                    doctors: {},
                    nurses: {}
                });
                alert(t('admin.evaluationsDeletedSuccess', 'تم حذف جميع التقييمات بنجاح'));
            } catch (err) {
                console.error('Error deleting evaluations:', err);
                alert(t('admin.evaluationsDeletedError', 'حدث خطأ أثناء حذف التقييمات'));
            }
        }
    };

    const handlePrint = () => {
        window.print();
    };


    const isNegativeAnswer = (field, value) => {
        const negativeValues = {
            parking: ['Yes'],
            waitingTime: ['Long'],
            generalAppearance: ['Bad'],
            doctorTime: ['No'],
            treatmentPlan: ['No'],
            nurseRating: ['Poor']
        };
        return negativeValues[field]?.includes(value);
    };


    const getBadgeVariant = (field, value) => {
        if (isNegativeAnswer(field, value)) return 'danger';

        const positiveValues = {
            parking: ['No'],
            waitingTime: ['Fast'],
            generalAppearance: ['Excellent'],
            doctorTime: ['Yes'],
            treatmentPlan: ['Yes'],
            nurseRating: ['Excellent']
        };

        if (positiveValues[field]?.includes(value)) return 'success';
        return 'warning';
    };


    const handleCopyPhone = (phone) => {
        if (phone) {
            navigator.clipboard.writeText(phone);
            alert(t('admin.phoneCopied', 'تم نسخ رقم الهاتف'));
        }
    };


    const translateValue = (value) => {
        if (!value) return value;

        const translationKeys = {
            'Yes': 'evaluation.options.yes',
            'No': 'evaluation.options.no',
            'Sometimes': 'evaluation.options.sometimes',
            'Fast': 'evaluation.options.fast',
            'Acceptable': 'evaluation.options.acceptable',
            'Long': 'evaluation.options.long',
            'Excellent': 'evaluation.options.excellent',
            'Bad': 'evaluation.options.bad',
            'A little': 'evaluation.options.aLittle',
            'Poor': 'evaluation.options.poor'
        };

        const key = translationKeys[value];
        return key ? t(key) : value;
    };

    if (loading) return <div className="text-center py-5">{t('common.loading')}</div>;
    if (!stats) return <div className="text-center py-5">{t('admin.noEvaluations')}</div>;


    const createChartData = (dataObj, label, colors) => {
        const labels = Object.keys(dataObj || {});
        const data = Object.values(dataObj || {});
        return {
            labels,
            datasets: [
                {
                    label,
                    data,
                    backgroundColor: colors || [
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(54, 162, 235, 0.6)',
                        'rgba(255, 206, 86, 0.6)',
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(153, 102, 255, 0.6)',
                    ],
                },
            ],
        };
    };

    const parkingData = createChartData(stats.parking, t('admin.parkingIssue'));
    const waitingTimeData = createChartData(stats.waitingTime, t('admin.waitingTime'));
    const generalAppearanceData = createChartData(stats.generalAppearance, t('admin.generalAppearance'));
    const doctorTimeData = createChartData(stats.doctorTime, t('admin.doctorTimeQuestion'));
    const treatmentPlanData = createChartData(stats.treatmentPlan, t('admin.treatmentPlanQuestion'));
    const nurseRatingData = createChartData(stats.nurseRating, t('admin.nurseRating'));
    const doctorCountsData = createChartData(stats.doctors, t('admin.doctorEvaluationsCount'));
    const nurseCountsData = createChartData(stats.nurses, t('admin.evaluationsPerNurse'));

    return (
        <Container className="py-5">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <BackButton to="/admin" />
                <h2 className="text-center flex-grow-1 mb-0">{t('admin.patientEvaluationStatistics')}</h2>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={handlePrint}
                    >
                        <i className="fas fa-print me-2"></i>
                        {t('admin.print', 'طباعة')}
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={handleDeleteAll}
                        disabled={evaluations.length === 0}
                    >
                        <i className="fas fa-trash-alt me-2"></i>
                        {t('admin.deleteAllEvaluations', 'حذف جميع التقييمات')}
                    </button>
                </div>
            </div>

            <Row className="g-4 mb-5">
                <Col md={6}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.nurseRating')}</Card.Title>
                            <Bar data={nurseRatingData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.doctorEvaluationsCount')}</Card.Title>
                            <Bar data={doctorCountsData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.doctorTimeQuestion')}</Card.Title>
                            <Pie data={doctorTimeData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.treatmentPlanQuestion')}</Card.Title>
                            <Pie data={treatmentPlanData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.generalAppearance')}</Card.Title>
                            <Pie data={generalAppearanceData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.waitingTime')}</Card.Title>
                            <Bar data={waitingTimeData} />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="shadow-sm">
                        <Card.Body>
                            <Card.Title className="text-center mb-3">{t('admin.parkingIssue')}</Card.Title>
                            <Pie data={parkingData} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <h3 className="mb-4 text-center">{t('admin.evaluationsLog')}</h3>

            { }
            <div className="filter-buttons-container mb-4">
                <button
                    className={`btn ${filterType === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => {
                        setFilterType('all');
                        setNegativeFilterType('all');
                    }}
                >
                    <i className="fas fa-list me-2"></i>
                    <span className="d-none d-sm-inline">{t('admin.allEvaluations', 'جميع التقييمات')}</span>
                    <span className="d-inline d-sm-none">الكل</span>
                    {' '}({evaluations.length})
                </button>
                <button
                    className={`btn ${filterType === 'negative' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFilterType('negative')}
                >
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <span className="d-none d-sm-inline">{t('admin.negativeEvaluations', 'التقييمات السلبية')}</span>
                    <span className="d-inline d-sm-none">سلبي</span>
                </button>
                <button
                    className={`btn ${filterType === 'positive' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => {
                        setFilterType('positive');
                        setNegativeFilterType('all');
                    }}
                >
                    <i className="fas fa-thumbs-up me-2"></i>
                    <span className="d-none d-sm-inline">{t('admin.positiveEvaluations', 'التقييمات الإيجابية')}</span>
                    <span className="d-inline d-sm-none">إيجابي</span>
                </button>
            </div>

            {/* فلتر فرعي للتقييمات السلبية */}
            {filterType === 'negative' && (
                <div className="mb-4 text-center">
                    <label className="me-2 fw-bold">
                        <i className="fas fa-filter me-2"></i>
                        {t('admin.filterByProblemType')}
                    </label>
                    <select
                        className="form-select d-inline-block w-auto"
                        value={negativeFilterType}
                        onChange={(e) => setNegativeFilterType(e.target.value)}
                    >
                        <option value="all">{t('admin.allProblems')}</option>
                        <option value="parking">{t('admin.parkingProblem')}</option>
                        <option value="waitingTime">{t('admin.waitingTimeProblem')}</option>
                        <option value="generalAppearance">{t('admin.appearanceProblem')}</option>
                        <option value="doctorTime">{t('admin.doctorTimeProblem')}</option>
                        <option value="treatmentPlan">{t('admin.treatmentPlanProblem')}</option>
                        <option value="nurseRating">{t('admin.nurseRatingProblem')}</option>
                    </select>
                </div>
            )}

   <Card className="shadow-sm border-0">
    <Card.Body className="p-0">
        <div className="table-responsive">
            <Table className="align-middle text-center mb-0 modern-table">
                <thead className="table-header-modern">
                    <tr>
                        <th className="border-0">#</th>
                        <th className="border-0">{t('admin.patientName')}</th>
                        <th className="border-0">{t('admin.phoneNumber')}</th>
                        <th className="border-0">{t('admin.status', 'الحالة')}</th>
                        <th className="border-0">{t('admin.date')}</th>
                        <th className="border-0">{t('admin.actions', 'الإجراءات')}</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredEvaluations.length === 0 ? (
                        <tr>
                            <td colSpan="6" className="text-center text-muted py-5">
                                <div className="empty-state">
                                    <i className="fas fa-inbox fa-3x mb-3 text-muted opacity-50"></i>
                                    <p className="mb-0">
                                        {filterType === 'negative'
                                            ? t('admin.noNegativeEvaluations', 'لا توجد تقييمات سلبية')
                                            : filterType === 'positive'
                                                ? t('admin.noPositiveEvaluations', 'لا توجد تقييمات إيجابية')
                                                : t('admin.noEvaluations')}
                                    </p>
                                </div>
                            </td>
                        </tr>
                    ) : (
                        filteredEvaluations.map((evalItem, index) => {
                            const hasNegative =
                                evalItem.parking === 'Yes' || 
                                evalItem.waitingTime === 'Long' ||
                                evalItem.generalAppearance === 'Bad' ||
                                evalItem.doctorTime === 'No' ||
                                evalItem.treatmentPlan === 'No' ||
                                evalItem.nurseRating === 'Poor';

                            return (
                                <React.Fragment key={evalItem.id}>
                                    {}
                                    <tr className={hasNegative ? 'table-row-negative' : 'table-row-positive'}>
                                        <td className="fw-bold text-primary">{index + 1}</td>
                                        <td>
                                            <div className="patient-name">
                                                <i className="fas fa-user-circle me-2 text-muted"></i>
                                                <strong>{evalItem.patientName || <span className="text-muted">-</span>}</strong>
                                            </div>
                                        </td>
                                        <td>
                                            {evalItem.phone ? (
                                                <div className="phone-display">
                                                    <span dir="ltr" className="phone-number">{evalItem.phone}</span>
                                                    <button
                                                        className="btn btn-sm btn-outline-primary copy-btn"
                                                        onClick={() => handleCopyPhone(evalItem.phone)}
                                                        title={t('admin.copyPhone', 'نسخ الرقم')}
                                                    >
                                                        <i className="fas fa-copy"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td>
                                            {hasNegative ? (
                                                <Badge bg="danger" className="status-badge">
                                                    <i className="fas fa-exclamation-circle me-1"></i>
                                                    {t('admin.hasNegativeFeedback', 'يحتوي على تقييم سلبي')}
                                                </Badge>
                                            ) : (
                                                <Badge bg="success" className="status-badge">
                                                    <i className="fas fa-check-circle me-1"></i>
                                                    {t('admin.positive', 'إيجابي')}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="text-muted">
                                            <i className="fas fa-calendar-alt me-2"></i>
                                            {new Date(evalItem.createdAt).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}
                                        </td>
                                        <td>
                                            <button
                                                className={`btn btn-sm ${expandedRow === evalItem.id ? 'btn-secondary' : 'btn-primary'} details-btn`}
                                                onClick={() => setExpandedRow(expandedRow === evalItem.id ? null : evalItem.id)}
                                            >
                                                <i className={`fas fa-${expandedRow === evalItem.id ? 'minus' : 'plus'}-circle me-1`}></i>
                                                <span className="d-none d-md-inline">
                                                    {expandedRow === evalItem.id ? t('admin.hideDetails', 'إخفاء') : t('admin.showDetails', 'التفاصيل')}
                                                </span>
                                            </button>
                                        </td>
                                    </tr>

                                    {}
                                    {evalItem.additionalNotes && (
                                        <tr className="notes-row">
                                            <td colSpan="6" className="text-start px-4 py-2 border-0 bg-light-alt">
                                                <div className="alert alert-light mb-1 py-2">
                                                    <strong>
                                                        <i className="fas fa-comment-dots me-2 text-primary"></i>
                                                        {t('admin.notes')}:
                                                    </strong>
                                                    <span className="ms-2 text-dark italic">{evalItem.additionalNotes}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {}
                                    {expandedRow === evalItem.id && (
                                        <tr>
                                            <td colSpan="6" className="p-3">
                                                <div className="compact-details bg-white border rounded p-3">
                                                    <table className="table table-sm table-borderless mb-0">
                                                        <tbody>
                                                            <tr>
                                                                <td className="fw-bold text-primary" width="25%">
                                                                    <i className="fas fa-user-md me-2"></i>{t('admin.doctorLabel')}:
                                                                </td>
                                                                <td width="25%">{evalItem.doctorName || '-'}</td>
                                                                <td className="fw-bold text-info" width="25%">
                                                                    <i className="fas fa-user-nurse me-2"></i>{t('admin.nurseLabel')}:
                                                                </td>
                                                                <td width="25%">{evalItem.nurseName || '-'}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-bold">{t('admin.treatmentPlanLabel')}:</td>
                                                                <td>
                                                                    <Badge bg={getBadgeVariant('treatmentPlan', evalItem.treatmentPlan)} className="badge-sm">
                                                                        {translateValue(evalItem.treatmentPlan) || '-'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="fw-bold">{t('admin.doctorTimeLabel')}:</td>
                                                                <td>
                                                                    <Badge bg={getBadgeVariant('doctorTime', evalItem.doctorTime)} className="badge-sm">
                                                                        {translateValue(evalItem.doctorTime) || '-'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-bold">{t('admin.nurseRatingLabel')}:</td>
                                                                <td>
                                                                    <Badge bg={getBadgeVariant('nurseRating', evalItem.nurseRating)} className="badge-sm">
                                                                        {translateValue(evalItem.nurseRating) || '-'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="fw-bold">{t('admin.parkingLabel')}:</td>
                                                                <td>
                                                                    <Badge bg={getBadgeVariant('parking', evalItem.parking)} className="badge-sm">
                                                                        {translateValue(evalItem.parking) || '-'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td className="fw-bold">{t('admin.waitingTimeLabel')}:</td>
                                                                <td>
                                                                    <Badge bg={getBadgeVariant('waitingTime', evalItem.waitingTime)} className="badge-sm">
                                                                        {translateValue(evalItem.waitingTime) || '-'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="fw-bold">{t('admin.generalAppearanceLabel')}:</td>
                                                                <td>
                                                                    <Badge bg={getBadgeVariant('generalAppearance', evalItem.generalAppearance)} className="badge-sm">
                                                                        {translateValue(evalItem.generalAppearance) || '-'}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                </tbody>
            </Table>
        </div>
    </Card.Body>
</Card>
        </Container>
    );
};

export default ManageEvaluations;
